import type { OnboardingStatusValue } from "@xop-any-time/shared";
import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material";
import type { JSX, ReactNode } from "react";

import { Backdrop } from "../login/Backdrop.js";
import { SiteFooter } from "../login/SiteFooter.js";
import { TopBar } from "../login/TopBar.js";

/**
 * The wall's chrome (spec E §10.2): the sign-in family's backdrop and top
 * bar, the company nameplate, a sign-out link, and a three-step stepper —
 * and deliberately NOT the app shell. An applicant has no rail, no counts,
 * and no avatar menu that offers /team; rendering that chrome would offer
 * eleven links that all 401.
 */

const STEPS = ["Sign the documents", "Under review", "Active"] as const;

function activeStep(status: OnboardingStatusValue): number {
  switch (status) {
    case "submitted":
      return 0;
    case "documents_submitted":
      return 1;
    case "active":
      return 2;
    case "rejected":
      return -1;
  }
}

export function OnboardingLayout({
  companyName,
  status,
  onSignOut,
  children,
}: {
  companyName: string;
  /** Null while the status is still loading — the stepper renders unlit. */
  status: OnboardingStatusValue | null;
  onSignOut: () => void;
  children: ReactNode;
}): JSX.Element {
  const step = status === null ? -1 : activeStep(status);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
        color: "text.primary",
        transition: "background-color 200ms, color 200ms",
      }}
    >
      <Backdrop />
      <TopBar />

      <Box
        component="main"
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          width: "100%",
          maxWidth: 880,
          mx: "auto",
          px: { xs: 2.5, sm: 5 },
          pt: { xs: 4, lg: 6 },
          pb: 4,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box>
            <Typography
              component="p"
              sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}
            >
              Reseller application
            </Typography>
            <Typography component="p" sx={{ mt: 0.25, fontSize: { xs: "1.5rem", sm: "1.9rem" }, fontWeight: 700, letterSpacing: "-0.02em" }}>
              {companyName}
            </Typography>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onSignOut}
            sx={{
              p: 0,
              border: 0,
              background: "none",
              font: "inherit",
              fontSize: 13,
              cursor: "pointer",
              color: "text.secondary",
              "&:hover": { color: "text.primary", textDecoration: "underline", textUnderlineOffset: 4 },
            }}
          >
            Sign out
          </Box>
        </Box>

        {step >= 0 && (
          <Stepper activeStep={step} alternativeLabel sx={{ mt: 4, mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        <Box sx={{ mt: step >= 0 ? 0 : 4 }}>{children}</Box>
      </Box>

      <SiteFooter />
    </Box>
  );
}

/** The one card surface the wall's steps render into. */
export function WallCard({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        px: { xs: 3, sm: 4.5 },
        py: { xs: 3.5, sm: 4.5 },
      }}
    >
      <Typography variant="h2" component="h2" sx={{ fontSize: { xs: "1.4rem", sm: "1.6rem" }, fontWeight: 600, letterSpacing: "-0.02em" }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/** The wall's footer line — the same contact the sign-in page points at. */
export const CHANNEL_MANAGER_LINE = "Questions? Contact your XOP Networks channel manager.";
